import {
  GetSimListSimAutoRenew,
  GetSimListSimStatusItem,
  GetSimListSimTypeItem,
  GetSimListSimVoiceOption,
} from "@/services/model";

import { additionalColumns, FilterDialogForm } from "./types";

export const FILTER_PARAM_KEYS = [
  "simStatus",
  "simAutoRenew",
  "simVoiceOption",
  "simType",
  "additionalColumns",
];

/**
 * Converts the current filter form state into URL search parameters.
 *
 * @param formState - The current state of the filter form.
 * @param searchParams - Existing search parameters to build upon. Defaults to a new empty instance.
 * @returns A new URLSearchParams object with the filter values applied.
 */
export const getFilterAndSearchParams = (
  formState: FilterDialogForm,
  searchParams: URLSearchParams = new URLSearchParams(),
) => {
  const updatedSearchParams = new URLSearchParams(searchParams.toString());

  // Handle SIM Status
  if (formState.simStatus && formState.simStatus.length > 0) {
    updatedSearchParams.set("simStatus", formState.simStatus.join(","));
  } else {
    updatedSearchParams.delete("simStatus");
  }

  // Handle Auto Renew
  if (formState.simAutoRenew) {
    if (formState.simAutoRenew === "ALL") {
      updatedSearchParams.delete("simAutoRenew");
    } else {
      updatedSearchParams.set("simAutoRenew", formState.simAutoRenew);
    }
  } else {
    updatedSearchParams.delete("simAutoRenew");
  }

  // Handle Voice Option
  if (formState.simVoiceOption) {
    if (formState.simVoiceOption === "ALL") {
      updatedSearchParams.delete("simVoiceOption");
    } else {
      updatedSearchParams.set("simVoiceOption", formState.simVoiceOption);
    }
  } else {
    updatedSearchParams.delete("simVoiceOption");
  }

  // Handle SIM Type
  if (formState.simType && formState.simType.length > 0) {
    updatedSearchParams.set("simType", formState.simType.join(","));
  } else {
    updatedSearchParams.delete("simType");
  }

  // Handle Additional Columns
  if (formState.additionalColumns && formState.additionalColumns.length > 0) {
    updatedSearchParams.set(
      "additionalColumns",
      formState.additionalColumns.join(","),
    );
  } else {
    updatedSearchParams.delete("additionalColumns");
  }

  return updatedSearchParams;
};

/**
 * Parses URL search parameters into a filter form state.
 *
 * @param searchParams - The URL search parameters to parse.
 * @returns The parsed filter form state.
 */
export const getFormStateFromSearchParams = (
  searchParams: URLSearchParams,
): FilterDialogForm => {
  const simStatus = (searchParams.get("simStatus")?.split(",") || [])
    .filter((v) => v !== "")
    .filter((v) =>
      Object.values(GetSimListSimStatusItem).includes(
        v as GetSimListSimStatusItem,
      ),
    ) as GetSimListSimStatusItem[];

  const rawSimAutoRenew = searchParams.get("simAutoRenew");
  const simAutoRenew = Object.values(GetSimListSimAutoRenew).includes(
    rawSimAutoRenew as GetSimListSimAutoRenew,
  )
    ? (rawSimAutoRenew as GetSimListSimAutoRenew)
    : "ALL";

  const rawSimVoiceOption = searchParams.get("simVoiceOption");
  const simVoiceOption = Object.values(GetSimListSimVoiceOption).includes(
    rawSimVoiceOption as GetSimListSimVoiceOption,
  )
    ? (rawSimVoiceOption as GetSimListSimVoiceOption)
    : "ALL";

  const simType = (searchParams.get("simType")?.split(",") || [])
    .filter((v) => v !== "")
    .filter((v) =>
      Object.values(GetSimListSimTypeItem).includes(v as GetSimListSimTypeItem),
    ) as GetSimListSimTypeItem[];

  const additionalColumnsList = (
    searchParams.get("additionalColumns")?.split(",") || []
  )
    .filter((v) => v !== "")
    .filter((v) =>
      (additionalColumns as ReadonlyArray<string>).includes(v),
    ) as (
    | "roamingDataConsumption"
    | "roamingOpenRuntime"
    | "roamingStatus"
    | "telephoneNumber"
    | "iccid"
  )[];

  return {
    simStatus,
    simAutoRenew,
    simVoiceOption,
    simType,
    additionalColumns: additionalColumnsList,
  };
};
